import React, { useState, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core';
import { theme } from 'src/theme';
import { FixMeLaterApi, Manufacturer } from 'src/types';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { useSnackBar } from 'src/hooks';
import { convertImageFileToBase64, normalizeToSqlDatetime } from 'src/utils';
import { DatePickerRangeComponentV2, SelectImageButton } from 'src/components';
import { WarrantyDetails } from '../components/WarrantyDetails';
import { RemarksDetails } from '../components/RemarksDetails';
import CircularProgress from '@material-ui/core/CircularProgress';
import { ManufacturerDetails } from 'src/redux/slices/manufacturer/types';

const {
  actions: manufacturerActions,
  selectors: manufacturerSelectors
} = slices.manufacturer;
interface Props {
  manufacturer: FixMeLaterApi;
  className?: string;
}

const useStyles = makeStyles(() => ({
  root: {},
  avatar: {
    height: 100,
    width: 100
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center'
  },
  imgContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center'
  },
  imgPreview: {
    width: undefined,
    height: 70,
    // marginRight: theme.spacing(1)
    marginBottom: theme.spacing(1)
  }
}));

const ManufacturerDetailView = ({
  className,
  manufacturer,
  ...rest
}: Props) => {
  const classes = useStyles();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const snackBar = useSnackBar();

  const detail = useAppSelector(
    manufacturerSelectors.selectManufacturerDetails
  );

  const [originalDetails, setOriginalDetails] = useState<Manufacturer>(
    manufacturer
  );

  const [isSaving, setIsSaving] = useState<boolean>(false);

  const onChangeManufacturerDetails = useCallback(
    // eslint-disable-next-line no-undef
    (update: Partial<ManufacturerDetails>) => {
      dispatch(manufacturerActions.updateManufacturerDetails(update));
    },
    [dispatch]
  );
  const getDirtyProps = (current: any, original: any): any => {
    const dirty: any = {};

    Object.keys(current).forEach((key) => {
      const currentValue = current[key];
      const originalValue = original[key];

      // simple shallow check
      if (currentValue !== originalValue) {
        dirty[key] = currentValue;
      }
    });

    return dirty;
  };

  const onSaveDetailPress = async () => {
    if (!id) {
      return;
    }

    setIsSaving(true);

    try {
      const payload = { ...getDirtyProps(detail, originalDetails), id: +id };

      const response = unwrapResult(
        await dispatch(manufacturerActions.updateManufacturerThunk(payload))
      );

      if (response?.success && response?.message) {
        snackBar.show({
          severity: 'success',
          message: response.message
        });

        getDetails();
      } else if (response?.errors) {
        const allMessages = Object.values(response?.errors)
          .flat()
          .join('\n');

        snackBar.show({
          severity: 'error',
          message: allMessages
        });
      } else {
        snackBar.show({
          severity: 'error',
          message: response?.message
            ? response.message
            : response?.errors?.[0]?.message
        });
      }
    } catch (error) {
      console.error('Save failed', error);
      snackBar.show({
        severity: 'error',
        message: 'Save failed. Please Check the Details before saving.'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const onImageSelected = async (img: File[], action: string) => {
    if (img && img?.length > 0) {
      const firstFile = img[0];
      const imgInBase64: any = await convertImageFileToBase64(firstFile);
      switch (true) {
        case action === 'icon':
          onChangeManufacturerDetails({
            icon_url: imgInBase64
          });
          break;
        case action === 'banner':
          onChangeManufacturerDetails({
            banner_url: imgInBase64
          });
          break;
      }
    }
  };

  const getDetails = useCallback(async () => {
    if (id) {
      const res = unwrapResult(
        await dispatch(manufacturerActions.getManufacturerViaIdThunk(+id))
      );
      if (res.success) {
        const manufacturer = res?.originalData?.manufacturer;
        manufacturerActions.setManufacturerDetails(manufacturer);
        setOriginalDetails(manufacturer);
      }
    }
  }, [dispatch, id]);

  const onDeleteManufacturer = async () => {
    if (!id) {
      return;
    }
    const deleteManufacturerResponse = unwrapResult(
      await dispatch(manufacturerActions.deleteManufacturerThunk(+id))
    );
    if (
      deleteManufacturerResponse?.success &&
      deleteManufacturerResponse?.message
    ) {
      // eslint-disable-next-line no-alert
      alert(deleteManufacturerResponse.message);
      navigate(-1);
    }
  };

  useEffect(() => {
    getDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box mt={2}>
      <Card className={clsx(classes.root, className)} {...rest}>
        <CardContent>
          {/* TODO: We can put permission here to see edit manufacturer details but no specific perms for that now.
            closest is canEditCategory from usePermissions */}
          <Box>
            <Card elevation={0}>
              <Grid container spacing={2}>
                <Grid item md={6} xs={12}>
                  <Typography color="textPrimary" gutterBottom variant="h5">
                    Manufacturer name
                  </Typography>
                  <TextField
                    fullWidth
                    required
                    name="manufacturerName"
                    variant="outlined"
                    onChange={(e) =>
                      onChangeManufacturerDetails({
                        name: String(e.target.value)
                      })
                    }
                    value={detail.name || ''}
                    style={{
                      height: 72
                    }}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <WarrantyDetails
                    warrantyDetails={detail.warranty_details || ''}
                    onChange={(val) =>
                      onChangeManufacturerDetails({ warranty_details: val })
                    }
                  />
                </Grid>
                <Grid item md={6} xs={12} className={classes.row}>
                  {detail.icon_url && (
                    <div className={classes.imgContainer}>
                      <img
                        alt="icon"
                        className={classes.imgPreview}
                        style={{
                          width: undefined,
                          height: 70
                          // marginRight: '1rem'
                        }}
                        src={detail.icon_url}
                      />
                    </div>
                  )}
                  <SelectImageButton
                    containerStyle={{ width: '100%' }}
                    title="Select/Update Manufacturer Icon"
                    helperText="Size Up to 3MB and only jpeg, jpg and png is allowed"
                    multiple={false}
                    onImageSelected={(e) => onImageSelected(e, 'icon')}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  {detail.banner_url && (
                    <div className={classes.imgContainer}>
                      <img
                        alt="banner"
                        className={classes.imgPreview}
                        style={{
                          width: 300, // this maybe changed depending on the resolution actually used
                          height: undefined,
                          marginBottom: '1rem'
                        }}
                        src={detail.banner_url}
                      />
                    </div>
                  )}
                  <SelectImageButton
                    containerStyle={{ width: '100%' }}
                    title="Select/Update Manufacturer Banner"
                    helperText="Size Up to 3MB and only jpeg, jpg and png is allowed"
                    multiple={false}
                    onImageSelected={(e) => onImageSelected(e, 'banner')}
                  />
                </Grid>
                <Grid item xs={12}>
                  <DatePickerRangeComponentV2
                    title="Brand Display Date"
                    fromDateValue={detail.brand_display_from}
                    toDateValue={detail.brand_display_to}
                    onChangeToDate={(date) => {
                      onChangeManufacturerDetails({
                        brand_display_to: normalizeToSqlDatetime(date)
                      });
                    }}
                    onChangeFromDate={(date) => {
                      onChangeManufacturerDetails({
                        brand_display_from: normalizeToSqlDatetime(date),
                        brand_display_to: normalizeToSqlDatetime(null)
                      });
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={!!detail.is_hidden_ecomm_website}
                        onChange={(_: any, checked) =>
                          onChangeManufacturerDetails({
                            is_hidden_ecomm_website: checked ? 1 : 0
                          })
                        }
                      />
                    }
                    label="Hidden on Ecomm"
                  />
                </Grid>
                <Grid item md={12} xs={12}>
                  <RemarksDetails
                    remarksDetails={detail.remarks || ''}
                    onChange={(val) =>
                      onChangeManufacturerDetails({ remarks: val })
                    }
                  />
                </Grid>
              </Grid>

              <Box display="flex" justifyContent="space-between" mt={2}>
                <Button
                  onClick={onDeleteManufacturer}
                  color="secondary"
                  variant="contained"
                  disabled={isSaving}
                  style={{ width: '201px', height: '50px' }}
                >
                  {isSaving ? <CircularProgress /> : 'Delete Manufacturer'}
                </Button>
                <Button
                  onClick={onSaveDetailPress}
                  color="primary"
                  variant="contained"
                  disabled={isSaving}
                  style={{ width: '129px', height: '50px' }}
                >
                  {isSaving ? <CircularProgress /> : 'Save details'}
                </Button>
              </Box>
            </Card>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ManufacturerDetailView;
